import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '../query-keys';
import { useLearningApi } from './use-learning-api';
import { UserType } from '../../../services/user-api';
import { useErrorContext } from '../../../components/error';
import { getCategoryDataByName } from '../../../utils/hardcodes';
import { QuizSession, SkillCategoryAssessment } from '../../../services/learning-api';
import {
  GetSkillGroupRespBody,
  GetSkillCategoriesRespBody,
} from '../../../services/learning-api/schema';

export type SkillCategory = GetSkillCategoriesRespBody['skillCategories'][0];

export const useGetSkillCategoryByRoleDepartment = (roleDepartmentId?: string) => {
  const learningApi = useLearningApi();
  return useQuery<SkillCategory[]>({
    queryKey: QUERY_KEYS.learning.skillCategory({
      roleDepartmentId: roleDepartmentId ?? '',
    }),
    enabled: !!roleDepartmentId,
    queryFn: () =>
      learningApi.skillCategory.getSkillCategoryByRoleDepartment(roleDepartmentId ?? ''),
    select: (data) => {
      return data.sort(
        (a, b) => getCategoryDataByName(a.title).order - getCategoryDataByName(b.title).order
      );
    },
  });
};

export const usePostSkillCategoryAssessment = (
  userType?: UserType,
  department?: string,
  roleDepartmentId?: string,
  options?: {
    onSuccess?: (updated: QuizSession) => void;
    onError?: (error: Error) => void;
  }
) => {
  const learningApi = useLearningApi();
  const queryClient = useQueryClient();
  const { setError } = useErrorContext();
  return useMutation({
    mutationKey: QUERY_KEYS.learning.postSkillCategoryAssessment({
      userType,
      department,
      roleDepartmentId,
    }),
    mutationFn: async (assessment: SkillCategoryAssessment): Promise<QuizSession> => {
      return learningApi.skillCategory.postSkillCategoryAssessment(
        assessment,
        userType,
        department,
        roleDepartmentId
      );
    },
    onSuccess: (updated: QuizSession) => {
      queryClient.setQueryData(
        QUERY_KEYS.learning.quizSession({ userType, department, roleDepartmentId }),
        updated
      );
      if (options?.onSuccess) {
        options.onSuccess(updated);
      }
    },
    onError: (error) => {
      setError({
        code: 500,
        message: error.message,
      });
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

export const useGetMultipleSkillCategoriesById = (skillCategoryIds: string[]) => {
  const learningApi = useLearningApi();

  return useQuery({
    queryKey: QUERY_KEYS.learning.getSkillGroup(skillCategoryIds),
    queryFn: async () => {
      const results = await Promise.all(
        skillCategoryIds.map((id) => learningApi.skillCategory.getSkillCategoryById(id))
      );
      return skillCategoryIds.reduce(
        (acc, id, index) => {
          acc[id] = {
            skillGroup: {
              skill_group_id: results[index].skillGroup.skill_group_id,
              title: results[index].skillGroup.title,
              ordinal: results[index].skillGroup.ordinal,
              colour: results[index].skillGroup.colour,
            },
            skill_category_ordinal: results[index].skill_category_ordinal,
            skill_category_title: results[index].skill_category_title,
          };
          return acc;
        },
        {} as Record<string, GetSkillGroupRespBody>
      );
    },
    enabled: skillCategoryIds.length > 0,
  });
};
